/**
 * "DELETE MY AUTHOR ACCOUNT" - `requestAccountDeletion` (D34.2; register R-016, lens U1-04).
 *
 * WHAT THE REQUEST IS. A 202: the platform ACCEPTS it and the erasure drain carries it out later,
 * on its next scheduled pass, not in this request (identity-account-author.controller.ts). The
 * profile is anonymised, the byline becomes a neutral label, published articles stay up unless a
 * takedown is asked for, and the money records are kept as accounting evidence. It cannot be
 * undone, and the screen says all of that BEFORE the button, not after.
 *
 * ★ A DIALOG, AND THE AUTHOR NAME TYPED INTO IT (design 24, T12). The confirmation used to be the
 * word "DELETE" typed inline, and a teammate typed it and sent a real request: the word alone did
 * not stop anybody. So the section keeps its explanation and one button, always on, which opens
 * "Delete your author account?": it says again what happens, holds the takedown box, and asks for
 * the author name - spaces at the ends and case ignored. An account with no author name yet (an
 * applicant may ask too, decision-log row 215 item 8) types "DELETE" instead. The dialog opens on
 * Cancel, as the cabinet's other destructive confirmations do (confirm-action.tsx); the contract's
 * own `confirm: true` then says the same thing to the server.
 *
 * THE NAME is the profile's, which Settings has read (`authorName`); until that read has answered,
 * or if it failed, the session's (`GET /v1/me`), so the section still does not wait on the profile.
 * The dialog shows the name it asks for, so the two sources cannot leave an author guessing.
 *
 * ★ THEN THE STATE THE API RETURNS, AND THE BUTTON IS GONE. The acknowledgement's own members are
 * drawn - the label the byline becomes, whether the articles stay, that money records are kept -
 * because "it is a promise the author is entitled to see before it happens". There is no read that
 * says a request is pending, so a second visit offers the button again; a second request is
 * answered 409 and drawn as "already requested", never as an error to retry.
 *
 * The session stays usable until the drain runs, so signing out is offered, not forced: the author
 * may still want to read a statement.
 *
 * ★ A BALANCE ABOVE $0.00 IS LOST WITH THE ACCOUNT (design 28, decision 5). The dialog shows the
 * warning of `docs/design/27-launch-texts/10-e-mails.md` (its last section) with the balance
 * `getPaymentsView` reads and the support address `getPlatformContacts` gives, and the Delete
 * button waits for its required box; the request then says `forfeitBalance: true`. At $0.00 or
 * below there is nothing to lose and nothing is shown or sent. A balance that cannot be read gets
 * 10's words for that, and the server decides: its 422 BALANCE_FORFEIT_CONFIRMATION_REQUIRED
 * reads the balance again and shows the warning, never an error.
 *
 * DESIGN 15 (the owner's live look at real cabinets, 2026-10-01): a section card (the kit's SectionCard)
 * with its icon in the danger colour; "the platform" is "MoonX" in what an author reads.
 */
import { Alert, Button, Checkbox, Group, List, Modal, Stack, Text, TextInput } from '@mantine/core';
import { Trash2 } from '@moonx/ui';
import { useCallback, useState } from 'react';
import { hasErrorCode, isApiProblem, useCabinetApi } from '../../features/cabinet/api';
import type { AccountDeletionAck, OperationResult } from '../../features/cabinet/api';
import { DELETION_ERROR_CODE } from '../../features/cabinet/api/problem';
import { ErrorState, SectionCard } from '../../features/cabinet/components';
import { formatMoney } from '../../features/cabinet/format';
import { useAsync } from '../../features/cabinet/state';
import type { AsyncResult } from '../../features/cabinet/state';
import { usePlatformContacts } from '../../features/support';
import { useAuthSession } from '../../features/auth/session/AuthSessionProvider';
import { Trans, useTranslation } from '../../i18n';

/**
 * What an account with no author name types, in English. Plain and short, and not the button's own
 * label. Each language has its own word (`profile` `settings.deletion.confirmToken`); the check is
 * this screen's alone, so the word can follow the cabinet's language.
 */
export const DELETION_CONFIRM_WORD = 'DELETE';

type Outcome =
  | { readonly kind: 'form' }
  | { readonly kind: 'sending' }
  | { readonly kind: 'accepted'; readonly ack: AccountDeletionAck }
  | { readonly kind: 'already-requested' }
  | { readonly kind: 'failed'; readonly error: unknown };

/** The destructive colour, as the cabinet's other destructive confirm draws it (confirm-action.tsx). */
const DANGER_BUTTON = {
  root: {
    backgroundColor: 'oklch(var(--mx-danger))',
    color: 'oklch(var(--mx-on-danger))',
  },
} as const;

/** The section's own button: the danger colour as an outline, since it only opens the dialog. */
const DANGER_OUTLINE = {
  root: {
    borderColor: 'oklch(var(--mx-danger))',
    color: 'oklch(var(--mx-danger))',
  },
} as const;

/**
 * What the balance read says about the deletion: still reading (nothing shown), cannot be read,
 * nothing to lose ($0.00 or below), or an amount above $0.00 that is lost with the account.
 */
type Balance =
  | { readonly kind: 'reading' }
  | { readonly kind: 'unreadable' }
  | { readonly kind: 'nothing-to-lose' }
  | { readonly kind: 'to-lose'; readonly amount: string };

function balanceOf(read: AsyncResult<OperationResult<'getPaymentsView'>>): Balance {
  if (read.status === 'error') {
    return { kind: 'unreadable' };
  }
  if (read.data === undefined) {
    return { kind: 'reading' };
  }
  return read.data.availableMinor > 0
    ? { kind: 'to-lose', amount: formatMoney(read.data.availableMinor, read.data.currency) }
    : { kind: 'nothing-to-lose' };
}

/** The name to type, or `null` when there is none to ask for. */
function nameToType(name: string | null | undefined): string | null {
  const trimmed = (name ?? '').trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * Spaces at the ends and case are ignored; anything else must match. Case folds by the cabinet's
 * language: Turkish "SİL" lower-cases to "sil" only under Turkish rules.
 */
function sameWords(typed: string, wanted: string, language: string): boolean {
  const fold = (value: string): string => value.trim().normalize('NFC').toLocaleLowerCase(language);
  return fold(typed) === fold(wanted);
}

export interface AccountDeletionSectionProps {
  /**
   * The author name from the profile Settings read: `null` when the profile has none, `undefined`
   * while that read has not answered (or failed), when the session's name stands in.
   */
  readonly authorName?: string | null | undefined;
}

export function AccountDeletionSection({ authorName }: AccountDeletionSectionProps) {
  const { t, i18n } = useTranslation('profile');
  const api = useCabinetApi();
  const { state, signOut } = useAuthSession();
  const [opened, setOpened] = useState(false);
  const [typed, setTyped] = useState('');
  const [takedown, setTakedown] = useState(false);
  const [forfeit, setForfeit] = useState(false);
  const [outcome, setOutcome] = useState<Outcome>({ kind: 'form' });
  const readPayments = useCallback((signal: AbortSignal) => api.getPaymentsView(signal), [api]);
  const payments = useAsync(readPayments, 'settings:deletion-balance');
  const contacts = usePlatformContacts();

  const name = nameToType(authorName === undefined ? state.session?.displayName : authorName);
  const balance = balanceOf(payments);
  const toLose = balance.kind === 'to-lose';
  const confirmWord = t('settings.deletion.confirmToken');
  const confirmed = sameWords(typed, name ?? confirmWord, i18n.language) && (!toLose || forfeit);
  const sending = outcome.kind === 'sending';
  // Shown as absent when it is not published (or not read): never a placeholder address.
  const supportEmail = contacts.status === 'ready' ? (contacts.data?.supportEmail ?? null) : null;
  const writeToUs = supportEmail === null ? t('settings.deletion.supportPage') : supportEmail;

  const close = (): void => {
    if (sending) {
      return;
    }
    setOpened(false);
    // Typed again on the next opening: the name is the deliberate act, not a setting to keep.
    setTyped('');
    setForfeit(false);
    if (outcome.kind === 'failed') {
      setOutcome({ kind: 'form' });
    }
  };

  const send = async (): Promise<void> => {
    setOutcome({ kind: 'sending' });
    try {
      const ack = await api.requestAccountDeletion({
        confirm: true,
        requestArticleTakedown: takedown,
        ...(toLose && forfeit ? { forfeitBalance: true } : {}),
      });
      setOutcome({ kind: 'accepted', ack });
      setOpened(false);
    } catch (error) {
      // Decision 5: the server holds a balance above $0.00 that this dialog did not show (read
      // before a tip arrived, or not read at all). Read it again and show the warning instead.
      if (hasErrorCode(error, DELETION_ERROR_CODE.balanceForfeitConfirmationRequired)) {
        setForfeit(false);
        setOutcome({ kind: 'form' });
        payments.refetch();
        return;
      }
      // The platform's answer to a repeat: "A deletion request for this account is already waiting
      // to be applied" (account-deletion.service.ts), a 409 with no domain code. Drawn as the state
      // it is, not as a failure to try again.
      if (isApiProblem(error) && error.status === 409) {
        setOutcome({ kind: 'already-requested' });
        setOpened(false);
      } else {
        setOutcome({ kind: 'failed', error });
      }
    }
  };

  return (
    <SectionCard title={t('settings.deletion.title')} icon={Trash2} tone="danger">
      <Stack gap="md">
        {outcome.kind === 'accepted' ? (
          <Alert variant="light" role="status" title={t('settings.deletion.accepted.title')}>
            <Stack gap="sm" align="flex-start">
              <List size="sm" spacing={4}>
                <List.Item>
                  <Trans
                    t={t}
                    i18nKey="settings.deletion.accepted.byline"
                    values={{ label: outcome.ack.anonymisedBylineLabel }}
                    components={{ strong: <strong /> }}
                  />
                </List.Item>
                <List.Item>
                  {outcome.ack.articlesRemainPublished === false
                    ? t('settings.deletion.accepted.takenDown')
                    : t('settings.deletion.accepted.stayUp')}
                </List.Item>
                <List.Item>{t('settings.deletion.records')}</List.Item>
                <List.Item>{t('settings.deletion.accepted.schedule')}</List.Item>
              </List>
              <Button
                variant="default"
                size="sm"
                onClick={() => {
                  void signOut();
                }}
              >
                {t('settings.deletion.accepted.signOut')}
              </Button>
            </Stack>
          </Alert>
        ) : outcome.kind === 'already-requested' ? (
          <Alert variant="light" role="status" title={t('settings.deletion.already.title')}>
            <Text size="sm">{t('settings.deletion.already.body')}</Text>
          </Alert>
        ) : (
          <>
            <Text size="sm">
              <Trans t={t} i18nKey="settings.deletion.intro" components={{ strong: <strong /> }} />
            </Text>
            <Group>
              <Button
                variant="outline"
                styles={DANGER_OUTLINE}
                onClick={() => {
                  setOpened(true);
                }}
              >
                {t('settings.deletion.open')}
              </Button>
            </Group>
          </>
        )}
      </Stack>

      <Modal opened={opened} onClose={close} title={t('settings.deletion.dialog.title')} size="md">
        <Stack gap="md">
          <List size="sm" spacing={4}>
            <List.Item>{t('settings.deletion.dialog.anonymized')}</List.Item>
            <List.Item>
              <Trans
                t={t}
                i18nKey="settings.deletion.dialog.byline"
                components={{ strong: <strong /> }}
              />
            </List.Item>
            <List.Item>{t('settings.deletion.dialog.articles')}</List.Item>
            <List.Item>{t('settings.deletion.records')}</List.Item>
          </List>
          <Text size="sm" fw={700}>
            {t('settings.deletion.dialog.cannotUndo')}
          </Text>
          {/* Decision 5, in the words of 27-launch-texts/10-e-mails.md (its last section). */}
          {balance.kind === 'to-lose' ? (
            <>
              <Alert
                variant="light"
                color="yellow"
                role="alert"
                title={t('settings.deletion.balance.title', { amount: balance.amount })}
              >
                <Text size="sm">{t('settings.deletion.balance.body', { address: writeToUs })}</Text>
              </Alert>
              <Checkbox
                label={t('settings.deletion.balance.confirm', { amount: balance.amount })}
                required
                checked={forfeit}
                onChange={(event) => {
                  setForfeit(event.currentTarget.checked);
                }}
                disabled={sending}
              />
            </>
          ) : balance.kind === 'unreadable' ? (
            <Alert variant="light" color="yellow" role="alert">
              <Text size="sm">{t('settings.deletion.unreadable', { address: writeToUs })}</Text>
            </Alert>
          ) : null}
          <Checkbox
            label={t('settings.deletion.takedown')}
            checked={takedown}
            onChange={(event) => {
              setTakedown(event.currentTarget.checked);
            }}
            disabled={sending}
          />
          <TextInput
            label={
              name === null
                ? t('settings.deletion.confirmWord', { word: confirmWord })
                : t('settings.deletion.confirmName', { name })
            }
            value={typed}
            onChange={(event) => {
              setTyped(event.currentTarget.value);
            }}
            autoComplete="off"
            disabled={sending}
          />
          {outcome.kind === 'failed' ? (
            <ErrorState title={t('settings.deletion.notSent')} error={outcome.error} />
          ) : null}
          <Group justify="flex-end">
            {/* ★ THE DIALOG OPENS ON CANCEL, never on the destructive choice
                (docs/ui/03-components.md:143) - `data-autofocus`, as confirm-action.tsx does. */}
            <Button variant="default" data-autofocus onClick={close} disabled={sending}>
              {t('settings.deletion.cancel')}
            </Button>
            <Button
              {...(confirmed ? { styles: DANGER_BUTTON } : {})}
              disabled={!confirmed}
              loading={sending}
              onClick={() => {
                void send();
              }}
            >
              {t('settings.deletion.delete')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </SectionCard>
  );
}
